import React, { useState, useEffect } from "react";
import PluginCard from "./PluginCard";
import { __ } from "@wordpress/i18n";

const PluginsList = () => {
	const [plugins, setPlugins] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	

	// Author username for fetching plugins
	const authorUsername = "Programmelab";

	useEffect(() => {
		fetchPlugins();
	}, []);

	const fetchPlugins = async () => {
		try {
			setLoading(true);
			// WordPress.org API endpoint for plugins by author
			const response = await fetch(
				`https://api.wordpress.org/plugins/info/1.2/?action=query_plugins&request[author]=${authorUsername}&request[per_page]=24`,
			);
			if (!response.ok) {
				throw new Error("Failed to fetch plugins");
			}
			
			const data = await response.json();
			setPlugins(data.plugins || []);
			setLoading(false);
		} catch (err) {
			setError(err.message);
			setLoading(false);
		}
	};

	const getCompatibilityMessage = (testedVersion) => {
		if (!testedVersion) return __("Unknown compatibility", "ultimate-security");
	
		const currentWPVersion = window.wp_version || "6.4"; // Default fallback
	
		const [testedMajor, testedMinor] = testedVersion.split(".").map(Number);
		const [currentMajor, currentMinor] = currentWPVersion.split(".").map(Number);
	
		if (testedMajor > currentMajor || (testedMajor === currentMajor && testedMinor >= currentMinor)) {
			return __("Compatible with your version of WordPress", "ultimate-security");
		} else {
			return __("Not tested with your current WordPress version", "ultimate-security");
		}
	};

	// Format the date to "X months ago"
	const formatLastUpdated = (lastUpdated) => {
		if (!lastUpdated) return "Unknown";

		// Convert the date string into a proper format that JavaScript can parse
		const parsedDate = new Date(
			lastUpdated.replace(
				/(\d{1,2}:\d{2})(am|pm)/i,
				(match, time, period) => {
					let [hours, minutes] = time.split(":").map(Number);
					if (period.toLowerCase() === "pm" && hours !== 12)
						hours += 12;
					if (period.toLowerCase() === "am" && hours === 12)
						hours = 0;
					return `${hours}:${minutes}`;
				},
			),
		);

		if (isNaN(parsedDate)) return "Invalid date";

		const currentDate = new Date();

		const diffMonths =
			(currentDate.getFullYear() - parsedDate.getFullYear()) * 12 +
			(currentDate.getMonth() - parsedDate.getMonth());

		if (diffMonths < 1) return "Less than a month ago";
		return `${diffMonths} month${diffMonths === 1 ? "" : "s"} ago`;
	};

	// Format active installs count
	const formatActiveInstalls = (count) => {
		if (!count || count < 10) return "Less Than 10 active installations";
		if (count < 100) return "Less Than 100 active installations";
		if (count < 1000) return "Less Than 1,000 active installations";
		if (count < 10000) return "Less Than 10,000 active installations";
		if (count < 100000) return "Less Than 100,000 active installations";
		if (count < 1000000) return "Less Than 1,000,000 active installations";
		return "1,000,000+ active installations";
	};

	if (loading)
		return <div className="text-center p-4">{__("Loading...", "ultimate-security")}</div>;
	if (error)
		return (
			<div className="text-center p-4 text-danger">{__("Error: ", "ultimate-security")} {error}</div>
		);
	if (plugins.length === 0)
		return <div className="text-center p-4">{__("No plugins found.", "ultimate-security")}</div>;

	return (
		<>
		
			<div className="other-plugins-container">
				<div className="row">
					{plugins.map((plugin) => {
						const formattedName = plugin.slug
							.split("-")
							.map(
								(word) =>
									word.charAt(0).toUpperCase() +
									word.slice(1),
							)
							.join(" ");

						return (
							<div key={plugin.slug} className="plugin-grid-item">
								<PluginCard
									slug={plugin.slug}
									Name={formattedName}
									Description={
										plugin.short_description.substring(
											0,
											100,
										) + "..."
									}
									Image={
										plugin.icons?.default ||
										plugin.icons?.["2x"] ||
										plugin.icons?.["1x"] ||
										"/api/placeholder/64/64"
									}
									ActiveInstallations={formatActiveInstalls(
										plugin.active_installs,
									)}
									LastUpdated={formatLastUpdated(
										plugin.last_updated,
									)}
									Compatibility={getCompatibilityMessage(plugin.tested)}
									NumberOfRatings={plugin.num_ratings}
									AuthorUrl={plugin.author_profile}
									PluginUrl={
										"https://wordpress.org/plugins/" +
										plugin.slug
									}
									DownloadLink={plugin.download_link}
									Rating={plugin.rating / 20}
								/>
							</div>
						);
					})}
				</div>
			</div>
		</>
	);
};

export default PluginsList;
